import React, { useState, useRef, useEffect } from 'react';
import { exposeTestId } from '../../config';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { selectLayoutState } from '../../store/selectors';
import { BirdState } from '../../store/bird/birdState';
import { useContextMenuHandlers } from '../hooks';
import { ElementDecorator } from '../ElementDecorator';
import { useCustomVisualOverlay } from '../CustomOverlay';
import { getFirstFocusable } from '@sas/nova-commons/util/focus';
import { useHasAncestorStatusOverlay } from '../StatusLayout';
import { usePromptLayoutStyles } from '../PromptLayoutDataProvider';
export const PositionedLayoutBase = (props) => {
    const { onKeyDown, onContextMenu } = useContextMenuHandlers(props.onReportContextMenu);
    useEffect(() => {
        if (props.autofocusMaximizeButton) {
            props.actions.setMaximizeAutofocusVisualId(null);
        }
    }, [props.autofocusMaximizeButton, props.actions]);
    const containerStyles = usePromptLayoutStyles(props.layoutState.promptConstraints);
    const style = Object.assign(Object.assign(Object.assign({}, props.layoutState.position), props.layoutState.style), { boxSizing: 'border-box', isolation: 'isolate' });
    if (props.autoLayout) {
        style.position = 'relative';
        style.flexShrink = 0;
        style.overflow = 'hidden';
        style.display = 'flex';
        if (props.layoutState.constraints) {
            const minContentWidth = props.layoutState.constraints.minWidth || 100;
            const minContentHeight = props.layoutState.constraints.minHeight || 0;
            const { padding = 0, borderWidth = 0 } = props.layoutState.style || {};
            const extraSpace = (padding + borderWidth) * 2;
            style.minWidth = minContentWidth + extraSpace;
            style.minHeight = minContentHeight + extraSpace;
        }
    }
    else {
        style.position = 'absolute';
    }
    Object.assign(style, props.style);
    if (style.position === 'absolute' && !props.layoutState.position) {
        // Avoid layout shift
        style.display = 'none';
    }
    const [hasMouseHover, setHasMouseHover] = useState(false);
    const CustomVisualOverlay = useCustomVisualOverlay();
    const ref = useRef(null);
    const refMouseDown = useRef(false);
    // If this PositionedLayout is the ancestor of another VisualElement that has a visible status overlay
    // then we may need to add a div to block mouse events to the child elements. This allows for the parent
    // container to show a status pill but not block mouse events, allowing for the leaf visuals to still
    // receive right-mouse clicks and activation of the context menu.  We don't block mouse events on intermediate
    // containers, as that would prevent the context menu behavior on the leaf elements that we want.
    const hasAncestorStatusOverlay = useHasAncestorStatusOverlay();
    let mouseBlockerDiv = null;
    if (hasAncestorStatusOverlay && !props.isContainer) {
        mouseBlockerDiv = (React.createElement("div", { style: { position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 } }));
    }
    return (React.createElement("div", { ref: ref, style: Object.assign(Object.assign({}, style), containerStyles), "data-testid": exposeTestId() ? props.id : null, 
        // Use onFocusCapture so the final focusVisual happens on the layout deepest in the hierarchy
        onFocusCapture: () => props.actions.focusVisual(props.id), 
        // TODO: Due to a ContextMenu bug, this may not be called when clicking away from a visual when the object toolbar is open
        onBlur: () => props.actions.focusVisual(null), onMouseEnter: () => setHasMouseHover(true), 
        // TODO: This is often not called for PromptContainers
        onMouseLeave: () => setHasMouseHover(false), onContextMenu: onContextMenu, onKeyDown: onKeyDown, 
        // Ensure onFocusCapture runs when a non-focusable child is clicked
        tabIndex: -1, onMouseDown: () => (refMouseDown.current = true), onMouseUp: () => (refMouseDown.current = false), onFocus: (e) => {
            var _a;
            if (e.target === ref.current && !refMouseDown.current) {
                (_a = getFirstFocusable(ref.current)) === null || _a === void 0 ? void 0 : _a.focus();
            }
        } },
        React.createElement("div", { style: {
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                outline: 'none',
            } },
            React.createElement("div", { style: {
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: props.layoutState.constraints
                        ? props.layoutState.constraints.preferredWidth
                        : undefined,
                    justifyContent: props.contentJustification,
                } }, props.children)),
        React.createElement(ElementDecorator, { id: props.id, hasMouseHover: hasMouseHover && !mouseBlockerDiv, autofocusMaximizeButton: props.autofocusMaximizeButton }),
        CustomVisualOverlay && React.createElement(CustomVisualOverlay, { id: props.id }),
        mouseBlockerDiv));
};
export const PositionedLayout = connect((state, props) => ({
    layoutState: selectLayoutState(state, props.id),
    autofocusMaximizeButton: state.bird.maximizeAutofocusVisualId === props.id,
    isContainer: !!state.birdElements.Container[props.id],
}), (dispatch) => ({
    actions: bindActionCreators(BirdState.actions, dispatch),
}))(PositionedLayoutBase);
